import { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ADMIN, STUDENT, TEACHER } from '../constants';
import { useLanguage } from '../hooks/useLanguage';
import api from '../services/axiosConfig';
import { AuthContext } from './AuthContextDefinition';

export interface AuthContextType {
  isAuthenticated: boolean;
  role: typeof STUDENT | typeof TEACHER | typeof ADMIN | null;
  roleName: string | null;
  userName: string | null;
  fullName: string | null;
  userId: number;
  organizationName: string | null;
  organizationType: number | null;
  organizationId: number | null;
  permissions: string[];
  loading: boolean;
  isInitial: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  resetPassword: (
    password: string,
    confirmPassword: string,
    pinOnly?: boolean,
  ) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { changeLanguage } = useLanguage();

  const handleLanguageChange = (usertype: number) => {
    if (usertype === TEACHER || usertype === ADMIN) {
      changeLanguage.mutate('ja');
    }
  };

  const handleNavigation = (usertype: number) => {
    if (usertype === TEACHER || usertype === ADMIN) {
      navigate('/students', { replace: true });
    } else {
      navigate('/top', { replace: true });
    }
  };

  const setAuthToken = (token: string | null) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  };

  const fetchUserData = async (token: string) => {
    const response = await api.get('/auth/users/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const { data: user, isLoading } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const userData = await fetchUserData(token);
      return userData;
    },
    retry: false,
    enabled: !!localStorage.getItem('token'),
  });

  const loginMutation = useMutation({
    mutationFn: async (token: string) => {
      setAuthToken(token);
      return fetchUserData(token);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['user'], data);
      handleLanguageChange(data.usertype);
      handleNavigation(data.usertype);
    },
    onError: () => {
      logout();
    },
  });

  // Password reset mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({
      password,
      confirmPassword,
      userId,
      pinOnly,
    }: {
      password: string;
      confirmPassword: string;
      userId: number;
      pinOnly?: boolean;
    }) => {
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await api.post(`${apiUrl}/auth/reset_password`, {
        password,
        confirm_password: confirmPassword,
        id: userId,
        pin_only: pinOnly ?? false,
      });
      return response.data;
    },
    onSuccess: async (usertype) => {
      const token = localStorage.getItem('token');
      if (token) {
        const userData = await fetchUserData(token);
        // Update cache
        queryClient.setQueryData(['user'], userData);

        handleNavigation(usertype);
      }
    },
  });

  // Login
  const login = async (token: string) => {
    await loginMutation.mutateAsync(token);
  };

  // Reset Password
  const resetPassword = async (
    password: string,
    confirmPassword: string,
    pinOnly?: boolean,
  ) => {
    if (!user?.id) {
      throw new Error('User ID is required to reset password');
    }

    await resetPasswordMutation.mutateAsync({
      password,
      confirmPassword,
      userId: user.id,
      pinOnly,
    });
  };

  // Logout
  const logout = () => {
    setAuthToken(null);
    queryClient.invalidateQueries({ queryKey: ['user'] });
    const organizationCode = localStorage.getItem('organizationCode');
    const redirectUrl = organizationCode
      ? `/login?organizationCode=${organizationCode}`
      : '/login';
    navigate(redirectUrl, { replace: true });
  };

  // Check if user has a specific permission
  const hasPermission = (permission: string) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  const value: AuthContextType = {
    isAuthenticated: !!user,
    role: user?.usertype ?? null,
    roleName: user?.role_name ?? null,
    userName: user?.username ?? null,
    fullName: user?.fullname ?? null,
    userId: user?.id ?? null,
    organizationName: user?.organization_name ?? null,
    organizationType: user?.organization_type ?? null,
    organizationId: user?.organization_id ?? null,
    permissions: user?.permissions ?? [],
    loading: isLoading,
    isInitial: user?.is_initial,
    login,
    logout,
    hasPermission,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
